/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ticket,
  AlertCircle,
  Clock,
  User,
  Filter,
  Search,
  Flag,
  MessageSquare,
  Calendar,
  RefreshCw,
  Settings,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout/DasboardLayout";
import { API_URL } from "../components/config";
import AgentStatusToggle from "./AgentStatusToggle";
import AssignmentSettingsPanel from "./AssignmentSettingsPanel";

const UserAllocatedTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPriority, setFilterPriority] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [agentDetails, setAgentDetails] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const adminId = localStorage.getItem("adminId") || localStorage.getItem("userid") || localStorage.getItem("userId");
  const navigate = useNavigate();

  const fetchTickets = async () => {
    try {
      setLoading(true);
      console.log("Fetching tickets for admin ID:", adminId);
      const response = await fetch(
        `${API_URL}/tickets/by-user?stage=assigned&user=${adminId}`
      );
      const data = await response.json();
      setTickets(data);
      setError(null);
    } catch (error) {
      setError("Failed to fetch tickets. Please try again later.");
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChats = async () => {
    try {
      console.log("Fetching chats for admin ID:", adminId);
      const response = await fetch(`${API_URL}/clientservicechat/admin/${adminId}`);
      const data = await response.json();
      setChats(data);
      setError(null);
    } catch (error) {
      setError("Failed to fetch chats. Please try again later.");
      console.error("Error fetching chats:", error);
    }
  };

  const fetchAgentDetails = async () => {
    try {
      console.log("Fetching agent details for admin ID:", adminId);
      const response = await fetch(`${API_URL}/users/${adminId}`);
      
      if (!response.ok) {
        console.error("Failed to fetch agent details:", response.status);
        setAgentDetails({
          userid: adminId,
          username: localStorage.getItem("username") || "Agent",
          role: "Support Agent",
          support_status: "busy",
          current_ticket_count: 0,
          current_chat_count: 0
        });
        return;
      }
      
      const data = await response.json();
      console.log("Agent details response:", data);
      
      if (data && data.length > 0) {
        setAgentDetails(data[0]);
      } else if (data && data.userid) {
        setAgentDetails(data);
      } else {
        setAgentDetails({
          userid: adminId,
          username: localStorage.getItem("username") || "Agent",
          role: "Support Agent",
          support_status: "waiting",
          current_ticket_count: 0,
          current_chat_count: 0
        });
      }
    } catch (error) {
      console.error("Error fetching agent details:", error);
      setAgentDetails({
        userid: adminId,
        username: localStorage.getItem("username") || "Agent",
        role: "Support Agent",
        support_status: "waiting",
        current_ticket_count: 0,
        current_chat_count: 0
      });
    }
  };

  useEffect(() => {
    console.log("localStorage values:", {
      adminId: localStorage.getItem("adminId"),
      userid: localStorage.getItem("userid"),
      userId: localStorage.getItem("userId"),
      userDetails: localStorage.getItem("userDetails")
    });

    if (adminId) {
      fetchTickets();
      fetchChats();
      fetchAgentDetails();
    } else {
      console.error("No adminId found in localStorage. Please log in again.");
      setError("No user ID found. Please log in again.");
      setLoading(false);
    }
  }, [adminId]);

  const handleTicketClick = (ticket) => {
    navigate(`/TicketManagement/${ticket.conversation_support_id}`);
  };

  const handleChatClick = (chat) => {
    navigate(`/CustomerServiceChat/${chat.client_service_chat_id}/${chat.customer_id}`);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchTickets(), fetchChats(), fetchAgentDetails()]);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const getPriorityColor = (level) => {
    switch (level) {
      case 1:
        return "text-red-600 bg-red-50";
      case 2:
        return "text-orange-600 bg-orange-50";
      case 3:
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getPriorityText = (level) => {
    switch (level) {
      case 1:
        return "High Priority";
      case 2:
        return "Medium Priority";
      case 3:
        return "Low Priority";
      default:
        return "Unknown Priority";
    }
  };

  const filteredTickets = tickets.filter(
    (ticket) =>
      (ticket.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.description?.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (filterPriority === "all" ||
        ticket.priority_level === parseInt(filterPriority))
  );

  const filteredChats = chats.filter(
    (chat) =>
      (chat.customer_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chat.client_service_chat_id?.toString().includes(searchTerm.toLowerCase()))
  );

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#002966]">
                Agent Dashboard
              </h1>
              <p className="text-gray-600 mt-2">
                Manage your assigned tickets and chats
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2 rounded-full hover:bg-gray-100 transition-all ${
                  showSettings ? "bg-[#ffc000]" : ""
                }`}
              >
                <Settings size={20} className="text-[#002966]" />
              </button>
              <button
                onClick={handleRefresh}
                className={`p-2 rounded-full hover:bg-gray-100 transition-all ${
                  refreshing ? "animate-spin" : ""
                }`}
              >
                <RefreshCw size={20} className="text-[#002966]" />
              </button>
            </div>
          </div>

          {/* Agent Status & Settings */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Agent Status */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-[#002966] mb-4">
                  Agent Status
                </h3>
                {agentDetails ? (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-[#ffc000] flex items-center justify-center">
                        <User className="text-[#002966]" size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-[#002966]">
                          {agentDetails.username}
                        </p>
                        <p className="text-sm text-gray-500">
                          {agentDetails.role}
                        </p>
                      </div>
                    </div>
                    <AgentStatusToggle 
                      agentId={adminId}
                      currentStatus={agentDetails.support_status}
                      onStatusChange={(newStatus) => {
                        setAgentDetails(prev => ({ ...prev, support_status: newStatus }));
                        fetchTickets();
                      }}
                    />
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>Current Tickets: <span className="font-medium">{agentDetails.current_ticket_count || 0}</span></p>
                      <p>Current Chats: <span className="font-medium">{agentDetails.current_chat_count || 0}</span></p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#002966] mx-auto mb-2"></div>
                    <p className="text-gray-500 text-sm">Loading agent details...</p>
                  </div>
                )}
              </div>
            </div>

            {/* Assignment Settings */}
            <div className="lg:col-span-2">
              {showSettings && <AssignmentSettingsPanel />}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex items-center">
              <AlertCircle size={20} className="mr-2" />
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002966] mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading your assignments...</p>
            </div>
          ) : (
            <div>
              {/* Chats Section */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-[#002966] mb-4">
                  Assigned Chats ({filteredChats.length})
                </h2>
                {filteredChats.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                    <MessageSquare size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No chats assigned
                    </h3>
                    <p className="text-gray-500">
                      You don't have any active chat assignments
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {filteredChats.map((chat) => (
                      <div
                        key={chat.client_service_chat_id}
                        className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden"
                      >
                        <div className="p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                <MessageSquare className="text-green-600" size={20} />
                              </div>
                              <div>
                                <h2 className="text-lg font-semibold text-[#002966]">
                                  Chat #{chat.client_service_chat_id}
                                </h2>
                                <span className="text-sm text-gray-500">
                                  Customer: {chat.customer_id}
                                </span>
                              </div>
                            </div>
                            <div className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                              Chat
                            </div>
                          </div>

                          <div className="space-y-3">
                            <p className="text-gray-600">
                              Assigned: {new Date(chat.assigned_at).toLocaleDateString()}
                            </p>

                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <div className="flex items-center">
                                <Clock size={16} className="mr-1" />
                                <span>
                                  {new Date(chat.date_created).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={() => handleChatClick(chat)}
                            className="mt-4 w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors duration-300 flex items-center justify-center"
                          >
                            <MessageSquare size={18} className="mr-2" />
                            Open Chat
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Tickets Section */}
              <div>
                <h2 className="text-2xl font-bold text-[#002966] mb-4">
                  Assigned Tickets ({filteredTickets.length})
                </h2>
                {filteredTickets.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                    <Ticket size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No tickets found
                    </h3>
                    <p className="text-gray-500">
                      No tickets match your current filters.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                    {filteredTickets.map((ticket) => (
                      <div
                        key={ticket.conversation_support_id}
                        className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden"
                      >
                        <div className="p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 rounded-full bg-[#ffc000] flex items-center justify-center">
                                <User className="text-[#002966]" size={20} />
                              </div>
                              <div>
                                <h2 className="text-lg font-semibold text-[#002966]">
                                  {ticket.customer_name}
                                </h2>
                                <span className="text-sm text-gray-500">
                                  ID: #{ticket.conversation_support_id}
                                </span>
                              </div>
                            </div>
                            <div
                              className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(
                                ticket.priority_level
                              )}`}
                            >
                              {getPriorityText(ticket.priority_level)}
                            </div>
                          </div>

                          <div className="space-y-3">
                            <p className="text-gray-600 line-clamp-2">
                              {ticket.description}
                            </p>

                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <div className="flex items-center">
                                <Clock size={16} className="mr-1" />
                                <span>
                                  {new Date(ticket.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center">
                                <MessageSquare size={16} className="mr-1" />
                                <span>{ticket.conversation_stage}</span>
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={() => handleTicketClick(ticket)}
                            className="mt-4 w-full bg-[#002966] text-white py-2 rounded-lg hover:bg-[#001f4d] transition-colors duration-300 flex items-center justify-center"
                          >
                            <Ticket size={18} className="mr-2" />
                            Handle Ticket
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UserAllocatedTickets;
